import React, { useState, useEffect, FocusEvent } from "react";
import { Calendar } from "lucide-react";
import { ISOString } from "src/types";
import { Tooltip } from "src/components/ui/tooltip";

interface DateInputBoxProps {
  id: string;
  inputDate?: ISOString;
  value?: ISOString;
  onInputChange?: (value: ISOString) => void;
  onChange?: (value: ISOString) => void;
  onFocusChange?: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onFocus?: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  disabled?: boolean;
  label?: string;
  required?: boolean;
  error?: boolean | string;
  className?: string;
  tooltipMessage?: string;
}

const DateInputBox: React.FC<DateInputBoxProps> = ({
  id,
  inputDate,
  value,
  onInputChange,
  onChange,
  onFocusChange,
  onFocus,
  onBlur,
  disabled = false,
  label,
  required = false,
  error,
  className = "",
  tooltipMessage
}) => {
  // Unified value handling
  const currentValue = inputDate ?? value ?? "";
  const currentOnChange = onInputChange ?? onChange;
  const hasError = typeof error === "string" ? !!error : !!error;
  const errorMessage = typeof error === "string" ? error : undefined;

  const [dateValue, setDateValue] = useState({ YYYY: "", MM: "", DD: "" });
  const [focusedField, setFocusedField] = useState<string | null>(null);

  useEffect(() => {
    if (currentValue) {
      const [YYYY, MM, DD] = currentValue.split("-").map((part) => part.trim());
      setDateValue({ YYYY, MM, DD });
    } else {
      setDateValue({ YYYY: "", MM: "", DD: "" });
    }
  }, [currentValue]);

  const handleDatePartChange = (part: "YYYY" | "MM" | "DD", newValue: string) => {
    if (disabled) return;

    // Only allow numeric input
    const numericValue = newValue.replace(/\D/g, "");

    const updatedDateValue = { ...dateValue, [part]: numericValue };
    setDateValue(updatedDateValue);

    const { YYYY, MM, DD } = updatedDateValue;

    if (!YYYY && !MM && !DD) {
      currentOnChange?.("");
      return;
    }

    const fullDate = `${YYYY}-${MM}-${DD}`;
    currentOnChange?.(fullDate as ISOString);
  };

  const handleFocus = (e: FocusEvent<HTMLInputElement>, field: string) => {
    setFocusedField(field);
    onFocusChange?.(e);
    onFocus?.(e);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    setFocusedField(null);
    onFocusChange?.(e);
    onBlur?.(e);
  };

  const baseInputClasses = `
    px-3 py-2.5 text-sm font-medium text-center text-secondary-900 bg-white border rounded-lg
    transition-all duration-200 ease-in-out placeholder-secondary-400
    focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
    disabled:bg-secondary-50 disabled:text-secondary-500 disabled:cursor-not-allowed
    ${
      hasError
        ? "border-error-300 focus:ring-error-500 focus:border-error-500"
        : "border-secondary-300 hover:border-secondary-400"
    }
  `
    .trim()
    .replace(/\s+/g, " ");

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <div className="flex items-center gap-2 mb-2">
          <label htmlFor={id} className="block text-sm font-medium text-secondary-700">
            {label}
            {required && <span className="text-error-500 ml-1">*</span>}
          </label>
          {tooltipMessage && <Tooltip message={tooltipMessage} iconSize={16} />}
        </div>
      )}

      <div className="relative">
        <div className="flex items-center gap-2">
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <Calendar className="w-4 h-4 text-secondary-400" />
          </div>

          <div className="flex items-center gap-2 pl-10 flex-1">
            {/* Day Input */}
            <input
              id={`${id}-dd`}
              type="text"
              inputMode="numeric"
              value={dateValue.DD}
              onChange={(e) => handleDatePartChange("DD", e.target.value)}
              onFocus={(e) => handleFocus(e, "DD")}
              onBlur={handleBlur}
              maxLength={2}
              placeholder="DD"
              disabled={disabled}
              className={`${baseInputClasses} w-16 ${focusedField === "DD" ? "shadow-lg" : "shadow-sm"}`}
            />

            <span className="text-secondary-400 font-medium">/</span>

            {/* Month Input */}
            <input
              id={`${id}-mm`}
              type="text"
              inputMode="numeric"
              value={dateValue.MM}
              onChange={(e) => handleDatePartChange("MM", e.target.value)}
              onFocus={(e) => handleFocus(e, "MM")}
              onBlur={handleBlur}
              maxLength={2}
              placeholder="MM"
              disabled={disabled}
              className={`${baseInputClasses} w-16 ${focusedField === "MM" ? "shadow-lg" : "shadow-sm"}`}
            />

            <span className="text-secondary-400 font-medium">/</span>

            {/* Year Input */}
            <input
              id={`${id}-yyyy`}
              type="text"
              inputMode="numeric"
              value={dateValue.YYYY}
              onChange={(e) => handleDatePartChange("YYYY", e.target.value)}
              onFocus={(e) => handleFocus(e, "YYYY")}
              onBlur={handleBlur}
              maxLength={4}
              placeholder="YYYY"
              disabled={disabled}
              className={`${baseInputClasses} w-20 ${focusedField === "YYYY" ? "shadow-lg" : "shadow-sm"}`}
            />
          </div>
        </div>
      </div>

      {errorMessage && <p className="mt-1 text-sm text-error-600">{errorMessage}</p>}
    </div>
  );
};

export { DateInputBox };
